import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { ChainHealthTable } from './ChainHealthTable';
import type { ChainHealth } from './types';

type Props = {
  chains: ChainHealth[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  /** Why the read failed, when it did. */
  errorMessage: string | null;
  onRecheck: () => void;
};

/**
 * Every chain crypto-api offers, read live with its own credentials (the reads a payment scan makes).
 * A failing scan is only a warning in crypto-api's logs, so a network its provider refuses shows here
 * before a customer pays on it. The testnets (sandbox stores) come in their own group.
 */
export function ChainHealthPanel({ chains, isLoading, isFetching, errorMessage, onRecheck }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const mainnets = (chains ?? []).filter((chain) => !chain.testnet);
  const testnets = (chains ?? []).filter((chain) => chain.testnet);
  const failingCount = (chains ?? []).filter((chain) => !chain.ok).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2">
            {t('chainHealth.title')}
            {failingCount > 0 && (
              <Badge variant="red">{t('chainHealth.failingCount', { count: failingCount })}</Badge>
            )}
          </CardTitle>
          <CardDescription>{t('chainHealth.description')}</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={onRecheck} disabled={isFetching}>
          <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {t('chainHealth.recheck')}
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        ) : errorMessage ? (
          <p className="text-destructive flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4" />
            {t('chainHealth.loadFailed', { error: errorMessage })}
          </p>
        ) : (
          <div className="space-y-6">
            <ChainHealthTable chains={mainnets} />
            {testnets.length > 0 && (
              <section className="space-y-2">
                <h3 className="text-sm font-medium">{t('chainHealth.testnetsTitle')}</h3>
                <ChainHealthTable chains={testnets} />
              </section>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
